import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { http, currency, fmtDate, API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { DownloadSimple, ArrowClockwise, ChartBar } from "@phosphor-icons/react";
import { toast } from "sonner";

const REPORTS = [
  { key: "online",         label: "Online users",       endpoint: "/reports/online",         dateFilter: false, cols: [["username","User"],["full_name","Name"],["ip_address","IP"],["phone","Phone"],["plan","Plan"],["last_seen_at","Last seen"]] },
  { key: "usage-sessions", label: "Usage / Session",    endpoint: "/reports/usage-sessions", dateFilter: true,  cols: [["username","User"],["started_at","Start"],["stopped_at","Stop"],["nas_ip","NAS"],["framed_ip","Framed IP"],["session_time_sec","Sec"],["input_mb","↓ MB"],["output_mb","↑ MB"],["status","Status"]] },
  { key: "renewals",       label: "Renewals",           endpoint: "/reports/renewals",       dateFilter: false, extra: "future-range", cols: [["subscriber","Subscriber"],["username","User"],["phone","Phone"],["plan","Plan"],["amount","Amount"],["expires_at","Expires"],["days_left","Days left"],["status","Status"]] },
  { key: "expired",        label: "Expired",            endpoint: "/reports/expired",        dateFilter: false, cols: [["subscriber","Subscriber"],["username","User"],["phone","Phone"],["plan","Plan"],["amount","Amount"],["expires_at","Expired on"],["overdue_days","Overdue days"],["status","Status"]] },
  { key: "new-subs",       label: "New subscriptions",  endpoint: "/reports/new-subs",       dateFilter: true,  cols: [["created_at","Created"],["username","User"],["full_name","Name"],["email","Email"],["phone","Phone"],["plan","Plan"],["status","Status"]] },
  { key: "sales",          label: "Sales",              endpoint: "/reports/sales",          dateFilter: true,  totalField: "total_amount", cols: [["paid_at","Paid at"],["invoice_no","Invoice"],["subscriber","Subscriber"],["username","User"],["amount","Amount"],["notes","Notes"]] },
  { key: "collections",    label: "Collections (day)",  endpoint: "/reports/collections",    dateFilter: true,  totalField: "total_amount", cols: [["date","Date"],["count","Count"],["amount","Amount"]] },
  { key: "statements",     label: "Statements",         endpoint: "/reports/statements",     dateFilter: false, needsSub: true, cols: [["created_at","Created"],["invoice_no","Invoice"],["amount","Amount"],["due_date","Due"],["paid_at","Paid at"],["status","Status"],["notes","Notes"]] },
  { key: "transactions",   label: "Transactions",       endpoint: "/reports/transactions",   dateFilter: true,  cols: [["created_at","Created"],["invoice_id","Invoice ID"],["amount","Amount"],["currency","Cur"],["provider","Provider"],["session_id","Session"],["status","Status"]] },
  { key: "messages",       label: "Message status",     endpoint: "/reports/messages",       dateFilter: true,  cols: [["ts","When"],["subscriber","Subscriber"],["phone","Phone"],["invoice_no","Invoice"],["days_before","Days"],["channel","Channel"],["status","Status"]] },
];

const isoDaysAgo = (n) => new Date(Date.now() - n * 86400000).toISOString().slice(0, 10);
const isoDaysAhead = (n) => new Date(Date.now() + n * 86400000).toISOString().slice(0, 10);
const todayIso = () => new Date().toISOString().slice(0, 10);

export default function Reports() {
  const [params, setParams] = useSearchParams();
  const initialTab = REPORTS.find((r) => r.key === params.get("tab"))?.key || REPORTS[0].key;
  const [active, setActive] = useState(initialTab);
  const [dateFrom, setDateFrom] = useState(isoDaysAgo(30));
  const [dateTo, setDateTo] = useState(todayIso());
  // Future-looking window used by Renewals (defaults to tomorrow → tomorrow so
  // the KPI click and the report agree). Kept separate from the past-looking
  // dateFrom/dateTo used by every other report.
  const [futureFrom, setFutureFrom] = useState(isoDaysAhead(1));
  const [futureTo, setFutureTo] = useState(isoDaysAhead(1));
  const [subs, setSubs] = useState([]);
  const [subId, setSubId] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const report = useMemo(() => REPORTS.find((r) => r.key === active), [active]);

  useEffect(() => { http.get("/subscribers").then((r) => setSubs(r.data)).catch(() => {}); }, []);

  // Keep the URL in sync with the active tab so Dashboard KPI deep-links work
  // both ways (arrive with ?tab=renewals AND survive a reload/share).
  useEffect(() => {
    if (params.get("tab") !== active) {
      const next = new URLSearchParams(params);
      next.set("tab", active);
      setParams(next, { replace: true });
    }
    // eslint-disable-next-line
  }, [active]);

  const buildParams = (extras = {}) => {
    const p = new URLSearchParams();
    if (report.dateFilter) { p.set("date_from", `${dateFrom}T00:00:00+00:00`); p.set("date_to", `${dateTo}T23:59:59+00:00`); }
    if (report.extra === "future-range") { p.set("date_from", `${futureFrom}T00:00:00+00:00`); p.set("date_to", `${futureTo}T23:59:59+00:00`); }
    for (const [k, v] of Object.entries(extras)) p.set(k, v);
    return p.toString();
  };

  const load = async () => {
    setLoading(true); setData(null);
    try {
      let path = report.endpoint;
      if (report.needsSub) {
        if (!subId) { setLoading(false); toast.error("Pick a subscriber for the statement"); return; }
        path = `${report.endpoint}/${subId}`;
      }
      const qs = buildParams();
      const { data } = await http.get(`${path}${qs ? "?" + qs : ""}`);
      setData(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load report");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [active]);

  const exportCsv = async () => {
    try {
      let path = report.endpoint;
      if (report.needsSub) {
        if (!subId) { toast.error("Pick a subscriber"); return; }
        path = `${report.endpoint}/${subId}`;
      }
      const qs = buildParams({ fmt: "csv" });
      const url = `${API}${path}${qs ? "?" + qs : ""}`;
      const token = localStorage.getItem("token");
      const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` }, credentials: "include" });
      const blob = await res.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `${report.key}-${todayIso()}.csv`;
      a.click();
      URL.revokeObjectURL(a.href);
    } catch { toast.error("CSV export failed"); }
  };

  const rows = data?.rows || [];
  const total = report.totalField ? data?.[report.totalField] : null;

  return (
    <div className="space-y-6 fade-up" data-testid="reports-view">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="uppercase-label">Business intelligence</div>
          <h1 className="font-display font-bold text-4xl mt-1">Reports</h1>
          <p className="text-sm text-muted-foreground mt-2 max-w-2xl">
            9 pre-built reports covering the day-to-day operations of an ISP — filter by date, drill into a subscriber's statement, export CSV in one click.
          </p>
        </div>
      </div>

      {/* Tabs */}
      <div className="border border-border bg-card">
        <div className="flex flex-wrap gap-1 p-2 border-b border-border">
          {REPORTS.map((r) => (
            <button
              key={r.key}
              onClick={() => setActive(r.key)}
              data-testid={`report-tab-${r.key}`}
              className={`text-xs font-mono uppercase px-3 py-1.5 border rounded-sm transition-colors ${active === r.key ? "border-primary text-primary bg-primary/10" : "border-transparent text-muted-foreground hover:text-foreground hover:bg-accent/40"}`}
            >{r.label}</button>
          ))}
        </div>

        {/* Filter bar */}
        <div className="p-4 border-b border-border flex items-end gap-3 flex-wrap">
          {report.dateFilter && (
            <>
              <Filter label="From"><Input data-testid="report-from" type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="h-9 bg-secondary" /></Filter>
              <Filter label="To"><Input data-testid="report-to" type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="h-9 bg-secondary" /></Filter>
            </>
          )}
          {report.extra === "future-range" && (
            <>
              <Filter label="From"><Input data-testid="report-future-from" type="date" value={futureFrom} onChange={(e) => setFutureFrom(e.target.value)} className="h-9 bg-secondary" /></Filter>
              <Filter label="To"><Input data-testid="report-future-to" type="date" value={futureTo} onChange={(e) => setFutureTo(e.target.value)} className="h-9 bg-secondary" /></Filter>
            </>
          )}
          {report.needsSub && (
            <Filter label="Subscriber">
              <select data-testid="report-sub" value={subId} onChange={(e) => setSubId(e.target.value)} className="h-9 bg-secondary border border-border px-3 text-sm rounded-sm min-w-[240px]">
                <option value="">— select —</option>
                {subs.map((s) => <option key={s.id} value={s.id}>{s.full_name} ({s.username})</option>)}
              </select>
            </Filter>
          )}
          <div className="ml-auto flex gap-2">
            <Button variant="outline" onClick={load} data-testid="report-refresh" className="rounded-sm border-border h-9"><ArrowClockwise size={14} className={`mr-2 ${loading ? "animate-spin" : ""}`} />Run</Button>
            <Button onClick={exportCsv} data-testid="report-csv" className="rounded-sm bg-primary hover:bg-primary/90 h-9"><DownloadSimple size={14} className="mr-2" />CSV</Button>
          </div>
        </div>

        {/* Summary strip */}
        <div className="p-4 border-b border-border flex items-center gap-6 text-xs font-mono flex-wrap">
          <div className="flex items-center gap-2"><ChartBar size={14} className="text-primary" /> <span className="uppercase-label">Rows</span> <span className="text-lg font-display" data-testid="report-count">{data?.count ?? 0}</span></div>
          {total !== null && total !== undefined && <div className="flex items-center gap-2"><span className="uppercase-label">Total</span> <span className="text-lg font-display text-primary" data-testid="report-total">{currency(total)}</span></div>}
          {data?.outstanding_amount !== undefined && <div className="flex items-center gap-2"><span className="uppercase-label">Outstanding</span> <span className="text-lg font-display text-[hsl(var(--warning))]" data-testid="report-outstanding">{currency(data.outstanding_amount)}</span></div>}
          {data?.subscriber && <div className="text-muted-foreground truncate">for <span className="text-foreground">{data.subscriber.full_name}</span> ({data.subscriber.username})</div>}
        </div>

        {/* Table */}
        <div className="overflow-x-auto max-h-[560px] overflow-y-auto" data-testid="report-table">
          <table className="w-full text-xs">
            <thead className="sticky top-0 bg-card z-10">
              <tr className="border-b border-border">
                {report.cols.map(([k, l]) => <th key={k} className="uppercase-label text-left py-2 px-3">{l}</th>)}
              </tr>
            </thead>
            <tbody className="font-mono">
              {loading && <tr><td colSpan={report.cols.length} className="text-center py-10 text-muted-foreground">loading…</td></tr>}
              {!loading && rows.length === 0 && <tr><td colSpan={report.cols.length} className="text-center py-10 text-muted-foreground">No data for the selected filters.</td></tr>}
              {!loading && rows.map((r, i) => (
                <tr key={i} className="border-b border-border/40 hover:bg-accent/20">
                  {report.cols.map(([k]) => (
                    <td key={k} className="py-1.5 px-3 truncate max-w-[240px]">{fmtCell(k, r[k])}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

const fmtCell = (k, v) => {
  if (v === undefined || v === null || v === "") return "—";
  if (k === "amount" || k === "input_mb" || k === "output_mb") return typeof v === "number" ? v.toLocaleString(undefined, { maximumFractionDigits: 2 }) : v;
  if (k.includes("_at") && typeof v === "string" && v.includes("T")) return fmtDate(v);
  return String(v);
};

const Filter = ({ label, children }) => (
  <div>
    <Label className="uppercase-label">{label}</Label>
    <div className="mt-1">{children}</div>
  </div>
);
